<template>
  <PanelCard v-if="visible" :title="I18n.t('user.panel.authentication_title')">
    <div
      v-if="capabilities.twoFactorVisible"
      class="tw-flex tw-flex-col tw-items-start tw-gap-2"
      data-testid="user-panel-two-factor"
    >
      <span class="tw-text-sm tw-font-medium tw-text-sf-slate-dark-2">
        {{ I18n.t('user.two_factor_authentication.title') }}
      </span>
      <span class="tw-text-sm tw-text-sf-slate-light-2">
        {{ twoFactorStatusLine }}
      </span>
      <span class="tw-text-sm tw-font-medium tw-text-sf-slate-dark-2">
        {{ I18n.t('user.panel.two_factor_reset_option') }}
      </span>
      <p class="tw-m-0 tw-text-sm tw-text-sf-slate-light-2">
        {{ I18n.t('user.panel.two_factor_reset_hint') }}
      </p>
      <SfButtonDs
        v-if="capabilities.canResetTwoFactor"
        variant="secondary"
        size="sm"
        data-testid="user-panel-reset-two-factor"
        @click="confirming = true"
      >
        {{ I18n.t('user.two_factor_authentication.reset.submit') }}
      </SfButtonDs>
    </div>

    <hr
      v-if="capabilities.twoFactorVisible && capabilities.ssoSupported"
      class="
        tw-mb-2
        tw-mt-4
        tw-w-full
        tw-border-x-0
        tw-border-b-0
        tw-border-t
        tw-border-solid
        tw-border-sf-slate-lighter-1
      "
    />

    <div
      v-if="capabilities.ssoSupported"
      class="tw-flex tw-flex-col tw-gap-4"
      data-testid="user-panel-sso"
    >
      <span class="tw-text-sm tw-font-medium tw-text-sf-slate-dark-2">
        {{ I18n.t('user.panel.sso_title') }}
      </span>
      <SfCheckbox
        v-model="enableSso"
        :label="I18n.t('user.form.sso_setting')"
        :disabled="capabilities.ssoLockedByVismaConnect"
        data-testid="user-panel-enable-sso"
      />
      <div v-if="enableSso && ssoEntities.length" class="tw-w-80">
        <PanelDropdown
          v-model="ssoEntityId"
          :label="I18n.t('user.panel.sso_entity_label')"
          :options="ssoEntityOptions"
          data-testid="user-panel-sso-entity"
        />
      </div>
    </div>

    <SfModal
      v-if="confirming"
      size="s"
      data-testid="user-panel-reset-two-factor-modal"
      @close="confirming = false"
    >
      <template #title>
        {{ I18n.t('user.two_factor_authentication.title') }}
      </template>
      <template #body>
        <p class="tw-m-0 tw-text-center tw-text-sm tw-text-sf-slate-dark-1">
          {{
            I18n.t('user.panel.two_factor_reset_confirm', {
              user_name: userName,
            })
          }}
        </p>
        <p class="tw-mb-0 tw-mt-4 tw-text-center tw-text-sm tw-text-sf-danger">
          {{
            I18n.t('user.two_factor_authentication.reset.modal.secondary_text')
          }}
        </p>
      </template>
      <template #footer>
        <div class="tw-flex tw-justify-center tw-gap-2">
          <SfButtonDs variant="secondary" @click="confirming = false">
            {{ I18n.t('common.cancel') }}
          </SfButtonDs>
          <SfButtonDs
            variant="danger"
            :loading="store.resetting"
            data-testid="user-panel-reset-two-factor-confirm"
            @click="resetTwoFactorAuthentication"
          >
            {{ I18n.t('user.two_factor_authentication.reset.modal.confirm') }}
          </SfButtonDs>
        </div>
      </template>
    </SfModal>
  </PanelCard>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'

import { SfTime } from '@app/adapters/time'
import {
  TWO_FACTOR_STATUS_KEYS,
  TWO_FACTOR_TOAST_ID,
} from '@app/application/user-management/constants'
import { twoFactorStatuses } from '@app/application/user-management/types'
import { useUserPanelStore } from '@app/application/user-management/stores/user-panel'
import useI18n from '@app/shared/vue/composables/use-I18n'
import usePanelToast from '@app/application/user-management/composables/use-panel-toast'
import PanelCard from '@app/application/user-management/components/panel-card.vue'
import PanelDropdown from '@app/application/user-management/components/user-panel/panel-dropdown.vue'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import SfCheckbox from '@app/shared/vue/components/sf-checkbox.vue'
import SfModal from '@app/shared/vue/components/sf-modal/sf-modal.vue'

defineOptions({ name: 'AuthenticationSection' })

const I18n = useI18n()
const store = useUserPanelStore()
const raiseToast = usePanelToast()
const { capabilities } = storeToRefs(store)

const confirming = ref(false)

const visible = computed(
  () => capabilities.value.twoFactorVisible || capabilities.value.ssoSupported
)

const userName = computed(() => store.user?.name ?? '')

const twoFactorStatusLine = computed(() => {
  const status = I18n.t(
    TWO_FACTOR_STATUS_KEYS[
      store.authentication?.twoFactorStatus ?? twoFactorStatuses.DISABLED
    ]
  )
  const updatedAt = store.authentication?.twoFactorUpdatedAt

  if (!updatedAt) return status

  return I18n.t('user.panel.two_factor_status_on', {
    status,
    date: SfTime(updatedAt).format('LL'),
  })
})

const ssoEntities = computed(() => store.authentication?.ssoEntities ?? [])

const ssoEntityOptions = computed(() =>
  ssoEntities.value.map((entity) => ({
    value: entity.id,
    label: entity.label,
  }))
)

const ssoEntityId = store.draftField('ssoEntityId')
const draftEnableSso = store.draftField('enableSso')

// The server reads SSO required without an entity as no SSO, or as the Visma
// Connect entity, and still answers with success.
const enableSso = computed({
  get: () => draftEnableSso.value,
  set: (value) => {
    draftEnableSso.value = value

    if (value && ssoEntityId.value === null && ssoEntities.value.length) {
      ssoEntityId.value = ssoEntities.value[0].id
    }
  },
})

async function resetTwoFactorAuthentication(): Promise<void> {
  const result = await store.resetTwoFactor()

  confirming.value = false

  if (result) {
    raiseToast(result.message, 'success', TWO_FACTOR_TOAST_ID)
  } else {
    raiseToast(
      I18n.t('user.panel.two_factor_reset_error'),
      'danger',
      TWO_FACTOR_TOAST_ID
    )
  }
}
</script>
